import React, { FC, ReactNode } from 'react';
import { FilePicker, FilePickerComponent, Modal, Paragraph } from '@wunder-mobility/ui-components';
import { FormikProvider, useFormik } from 'formik';
import { Trans } from '@lingui/react/macro';
import ModalActionsFooter from '@/core/components/UI/ModalActionsFooter';
import { useRequestAssetCreateMutation } from '@/core/services/Media.service';
import { UploadedFile } from '@/core/types';
import { ProvidedModal } from '@/core/components/UI/modals/ModalProvider';

export interface FileCreateModalT extends ProvidedModal {
  accept?: FilePickerComponent['accept'];
  helperText?: ReactNode;
  onFilesCreated: (files: (UploadedFile | undefined)[]) => void;
  description?: ReactNode;
}

interface FilesForm {
  files: File[];
}

const FileCreateModal: FC<FileCreateModalT> = ({
  accept,
  helperText,
  onFilesCreated,
  opened,
  onCancel,
  description,
}) => {
  const [createAsset] = useRequestAssetCreateMutation();

  const onSubmit = async (values: FilesForm) => {
    try {
      const { files } = values;
      const assets = await Promise.all(
        files.map(async (file) => {
          if (file instanceof Blob) {
            return createAsset(file as Blob).unwrap();
          }
        }),
      );

      onCancel?.();
      return onFilesCreated(assets!);
    } catch (e) {
      return e;
    }
  };

  const form = useFormik<FilesForm>({
    initialValues: {
      files: [],
    },
    onSubmit,
  });

  return (
    <FormikProvider value={form}>
      <Modal
        variation="default"
        onClose={onCancel!}
        opened={opened!}
        modalHeader={<Trans id="components.fileCreateModal.addFiles">Add files</Trans>}
        modalFooter={<ModalActionsFooter onClose={onCancel} />}
      >
        {description && (
          <Paragraph size={3} textColor="text-600">
            {description}
          </Paragraph>
        )}
        <FilePicker
          maxFiles={1}
          helperText={
            helperText ?? (
              <Trans id="component.fileCreateModal.allowedDefault">
                PDF, PNG, JPG and JPEG allowed
              </Trans>
            )
          }
          accept={
            accept ?? { 'image/jpeg': ['.jpg', '.png', '.jpeg'], 'application/pdf': ['.pdf'] }
          }
          onChange={(files) => {
            form.setFieldValue('files', files);
          }}
        />
      </Modal>
    </FormikProvider>
  );
};

export default FileCreateModal;
